import React from "react";
import { useParams } from "react-router-dom";
import Table from "components/Table/Table";
import { PaginationBar } from "components/Pagination";
import PageSizeSelect from "components/Table/PageSizeSelect";
import { useTableView } from "components/Table/useTableView";
import { useAuth0 } from "@auth0/auth0-react";
import { useActiveOrg } from "context/ActiveOrgContext";
import Button from "components/Button/Regular";
import vgToast from "utils/toast";
import BookingDialog, {
  BookingSubmission,
} from "pages/calendar/BookingDialog";
import {
  Booking,
  createBooking,
  readPatientBookings,
} from "services/bookings.service";
import { readBookableConfigs } from "services/booking-configs.service";
import { readAnimalCompleteByAnimalId, readAnimal } from "services/patients.service";
import { createInvoiceFromBooking } from "services/invoices.service";
import ConfirmDialog from "components/ConfirmDialog";
import { getOrderStateLabel, ORDER_STATE } from "constants/bookingEnums";

type Row = {
  id: string;
  when: string;
  booking: string;
  client: string;
  status: string;
};

type Option = { title: string; value: string };

function contactName(raw: unknown) {
  const row = raw as Record<string, unknown>;
  const first = String(row.contactFirstName ?? row.ContactFirstName ?? "").trim();
  const last = String(row.contactLastName ?? row.ContactLastName ?? "").trim();
  const full = String(row.contactFullName ?? row.ContactFullName ?? "").trim();
  return full || [first, last].filter(Boolean).join(" ") || "Unknown";
}

function contactId(raw: unknown) {
  const row = raw as Record<string, unknown>;
  return String(row.contactId ?? row.ContactId ?? "");
}

/** "9 Aug 2026, 14:00 – 14:30", collapsing the date when both ends share one. */
function formatWhen(booking: Booking) {
  const start = booking.start ? new Date(booking.start) : null;
  const end = booking.end ? new Date(booking.end) : null;
  if (!start || Number.isNaN(start.getTime())) return "—";

  const date = start.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  const time = (d: Date) =>
    d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });

  if (!end || Number.isNaN(end.getTime())) return `${date}, ${time(start)}`;
  return `${date}, ${time(start)} – ${time(end)}`;
}

/** Local datetime-local string, which is what the dialog's inputs expect. */
function toDateTimeLocal(d: Date) {
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

/** The next round half-hour, so the default slot is a plausible one. */
function nextSlot() {
  const start = new Date();
  start.setSeconds(0, 0);
  start.setMinutes(start.getMinutes() > 30 ? 60 : 30);
  const end = new Date(start.getTime() + 30 * 60 * 1000);
  return { start: toDateTimeLocal(start), end: toDateTimeLocal(end) };
}

/**
 * A patient's bookings, and the second way to make one - the calendar being the
 * first. Booking from here fixes the patient, so the animal named at the top of
 * the page is the animal that gets booked.
 */
const BookingsList = () => {
  const { id } = useParams();
  const { orgId } = useActiveOrg();
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();

  const animalId = id ? Number(id) : NaN;
  const canLoad =
    isAuthenticated && orgId > 0 && Number.isFinite(animalId) && animalId > 0;

  const [bookings, setBookings] = React.useState<Booking[]>([]);
  const [patientName, setPatientName] = React.useState("this patient");
  const [owners, setOwners] = React.useState<Option[]>([]);
  const [configNames, setConfigNames] = React.useState<Record<number, string>>({});
  const [clientNames, setClientNames] = React.useState<Record<number, string>>({});
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [slot, setSlot] = React.useState(nextSlot);
  const [saving, setSaving] = React.useState(false);
  const [toInvoice, setToInvoice] = React.useState<Booking | null>(null);
  const [invoicing, setInvoicing] = React.useState(false);

  const load = React.useCallback(async () => {
    if (!canLoad) {
      setBookings([]);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      const [bookingRes, animalRes, ownerRes, configRes] = await Promise.all([
        readPatientBookings(token, orgId, animalId),
        readAnimal(token, orgId, animalId),
        readAnimalCompleteByAnimalId(token, orgId, animalId),
        readBookableConfigs(token, orgId),
      ]);

      if (bookingRes.error) {
        setError(bookingRes.error.message);
        setBookings([]);
        return;
      }
      setBookings(bookingRes.data ?? []);

      const name = animalRes.data?.name;
      if (name) setPatientName(name);

      const contacts = ownerRes.data?.contactList ?? [];
      const ownerOptions = contacts.map((c) => ({
        title: contactName(c),
        value: contactId(c),
      }));
      setOwners(ownerOptions);
      setClientNames(
        Object.fromEntries(
          ownerOptions
            .filter((o) => Number(o.value) > 0)
            .map((o) => [Number(o.value), o.title])
        )
      );
      setConfigNames(
        Object.fromEntries(
          (configRes.data ?? []).map((c) => [
            c.serviceFulfilmentConfigId,
            c.name || `Configuration ${c.serviceFulfilmentConfigId}`,
          ])
        )
      );
    } catch (e) {
      setError((e as Error).message);
      setBookings([]);
    } finally {
      setLoading(false);
    }
  }, [canLoad, getAccessTokenSilently, orgId, animalId]);

  React.useEffect(() => {
    load();
  }, [load]);

  const openDialog = () => {
    setSlot(nextSlot());
    setDialogOpen(true);
  };

  const handleSubmit = async (values: BookingSubmission) => {
    setSaving(true);
    try {
      const token = await getAccessTokenSilently();
      const { error: apiError } = await createBooking(token, {
        orgId,
        contactId: values.contactId,
        animalId,
        serviceId: values.serviceId,
        serviceFulfilmentConfigId: values.serviceFulfilmentConfigId,
        start: new Date(values.start).toISOString(),
        end: new Date(values.end).toISOString(),
      });
      if (apiError) {
        vgToast.error(apiError.message);
        return;
      }
      vgToast.success("Booking created.");
      setDialogOpen(false);
      await load();
    } catch (e) {
      vgToast.error((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  /** The row's action. Already-invoiced bookings are not offered again. */
  const requestInvoice = (serviceOrderId: string) => {
    const booking = bookings.find((b) => String(b.serviceOrderId) === serviceOrderId);
    if (!booking) return;
    if (booking.orderState === ORDER_STATE.INVOICED) {
      vgToast.error("That booking has already been invoiced.");
      return;
    }
    setToInvoice(booking);
  };

  const confirmInvoice = async () => {
    if (!toInvoice) return;

    setInvoicing(true);
    try {
      const token = await getAccessTokenSilently();
      const { error: apiError } = await createInvoiceFromBooking(
        token,
        orgId,
        toInvoice.serviceOrderId
      );
      if (apiError) {
        vgToast.error(apiError.message);
        return;
      }
      vgToast.success("Invoice raised.");
      setToInvoice(null);
      await load();
    } catch (e) {
      vgToast.error((e as Error).message);
    } finally {
      setInvoicing(false);
    }
  };

  const rows: Row[] = React.useMemo(
    () =>
      bookings.map((b) => ({
        id: String(b.serviceOrderId),
        when: formatWhen(b),
        booking:
          configNames[b.serviceFulfilmentConfigId ?? 0] ??
          (b.serviceFulfilmentConfigId ? `Configuration ${b.serviceFulfilmentConfigId}` : "—"),
        client: clientNames[b.contactId ?? 0] ?? (b.contactId ? `Client ${b.contactId}` : "—"),
        status: getOrderStateLabel(b.orderState),
      })),
    [bookings, configNames, clientNames]
  );

  // Client-side paging: the org read returns every booking at once.
  const view = useTableView(rows);

  return (
    <>
      <div className="flex items-center justify-between text-sm text-gray-400 m-4">
        <PageSizeSelect value={view.pageSize} onChange={view.setPageSize} />
        <Button color="Primary" onClick={openDialog}>
          <span>New Booking</span>
        </Button>
      </div>

      <Table<Row>
        loading={loading}
        columns={{
          when: "When",
          booking: "Booking",
          client: "Client",
          status: "Status",
        }}
        rows={view.pageRows}
        sort={view.sort}
        onSortChange={view.toggleSort}
        handleViewItem={requestInvoice}
        emptyMessage={
          loading ? "Loading bookings…" : "This patient has no bookings yet."
        }
      />

      {error && <div className="px-4 pb-2 text-sm text-red-500">{error}</div>}

      <PaginationBar
        from={view.from}
        to={view.to}
        total={view.total}
        currentPage={view.page}
        totalPages={view.totalPages}
        onPrev={view.goPrev}
        onNext={view.goNext}
        onPageSelect={view.goToPage}
      />

      <ConfirmDialog
        open={toInvoice !== null}
        title="Raise an invoice for this booking?"
        description={
          toInvoice
            ? `This bills the client for ${
                configNames[toInvoice.serviceFulfilmentConfigId ?? 0] ?? "this booking"
              } and marks the booking as invoiced.`
            : ""
        }
        confirmLabel="Raise invoice"
        busyLabel="Raising…"
        busy={invoicing}
        onCancel={() => {
          if (!invoicing) setToInvoice(null);
        }}
        onConfirm={confirmInvoice}
      />

      <BookingDialog
        open={dialogOpen}
        initialStart={slot.start}
        initialEnd={slot.end}
        busy={saving}
        lockedPatient={
          Number.isFinite(animalId) && animalId > 0
            ? { animalId, name: patientName }
            : undefined
        }
        // Only the patient's registered owners: booking an animal against an
        // unrelated client is a mistake worth making impossible from here.
        clientOptions={owners.length > 0 ? owners : undefined}
        initialContactId={owners.length === 1 ? owners[0].value : undefined}
        onCancel={() => {
          if (!saving) setDialogOpen(false);
        }}
        onSubmit={handleSubmit}
      />
    </>
  );
};

export default BookingsList;
